import {ObjectFT} from "#template/nodes/object/fundamental"
import {MeshFT} from "#template/nodes/object/mesh/fundamental"
import {CurveFT} from "#template/nodes/object/curve/fundamental"
import {MaterialFT} from "#template/nodes/material/fundamental"
import {ImageFT} from "#template/nodes/image/fundamental"
import {TemplateGraphFT} from "#template/nodes/template-graph/fundamental"
import {TransformFT} from "#template/nodes/transform/fundamental"
import {StringFT} from "#template/nodes/string/fundamental"
import {NumberFT} from "#template/nodes/number/fundamental"
import {BooleanFT} from "#template/nodes/boolean/fundamental"
import {JsonFT} from "#template/nodes/json/fundamental"
import {getRegisteredNode} from "@cm/graph-v3"
import {classHasMarker, valueMarker} from "#template/role-markers"

// Derived FTs (Mesh, Curve) come before their parent (Object) so dispatch matches most-specific first.
export const fundamentalTypes = {
    Mesh: MeshFT,
    Curve: CurveFT,
    Object: ObjectFT,
    Material: MaterialFT,
    TemplateGraph: TemplateGraphFT,
    Image: ImageFT,
    Transform: TransformFT,
    String: StringFT,
    Number: NumberFT,
    Boolean: BooleanFT,
    Json: JsonFT,
} as const

export type FundamentalTypeName = keyof typeof fundamentalTypes

// Union over the actually-registered FTs. Prefer this over the widened `FundamentalType<…unknown…>`
// at iteration boundaries: each FT is invariant in `TLike`, so the widened form isn't a supertype
// of the specific entries and requires unsound casts.
export type AnyFundamentalType = (typeof fundamentalTypes)[FundamentalTypeName]

export const fundamentalTypeList: ReadonlyArray<AnyFundamentalType> = Object.values(fundamentalTypes)

// nodeClass -> owning FT, built lazily from each FT's own role classes (rename-safe — no `${name}Role`
// string reconstruction). The map covers Input/Output/Switch/Export/Likes; value nodes aren't part of the
// FT object, so they fall through to a marker check (they carry both `valueMarker` and their FT marker).
let nodeClassToFundamentalType: Map<string, AnyFundamentalType> | undefined
export const fundamentalTypeForNodeClass = (nodeClass: string): AnyFundamentalType | undefined => {
    if (!nodeClassToFundamentalType) {
        nodeClassToFundamentalType = new Map()
        for (const ft of fundamentalTypeList)
            for (const cls of [ft.Likes, ft.Switch, ft.Input, ft.Output, ft.Export]) nodeClassToFundamentalType.set(cls.getNodeClass(), ft)
    }
    const direct = nodeClassToFundamentalType.get(nodeClass)
    if (direct) return direct

    const ctor = (() => {
        try {
            return getRegisteredNode(nodeClass, "template")
        } catch {
            return undefined
        }
    })()
    if (!ctor || !classHasMarker(ctor, valueMarker)) return undefined
    return fundamentalTypeList.find((ft) => classHasMarker(ctor, ft.markers[0]))
}

export {isFundamentalLikeNode, isFundamentalLikeNodeClass, fundamentalLikeNode} from "#template/role-markers"
